import React from "react";
import { Clock } from "../../../components/icons/svg/outline";
import {
  formatDashboardCount,
  formatDashboardDelta,
  formatDashboardHours,
} from "../../../utils/timekeeping";
import PrimaryJobChip from "./PrimaryJobChip";
import {
  getPayoutTimesheetStatusDisplay,
  PAYOUT_TIMESHEET_COLUMNS,
  PAYOUT_TIMESHEET_TABLE,
  PAYOUT_TIMESHEET_TOOLTIPS,
} from "./payoutTimesheetTableConfig";
import { isNoHoursPayoutDashboardRow } from "./summaryUtils";

/**
 * Formats a signed hour delta for compact dashboard display.
 *
 * @param {number} value - Hour delta.
 * @returns {string} Formatted delta text.
 */
function formatDeltaDisplay(value) {
  return Number(value) === 0 ? "0" : formatDashboardDelta(value);
}

/**
 * Derives display flags and values used by payout dashboard cells.
 *
 * @param {Object} timesheet - Dashboard timesheet row.
 * @param {boolean} quickBooksExported - Whether QuickBooks was exported for the week.
 * @returns {Object} Derived cell display state.
 */
function getRowDisplayState(timesheet, quickBooksExported = false) {
  const hasWorkerHours =
    timesheet.workerHours !== null && timesheet.workerHours !== undefined;
  const hasManagerHours = Number(timesheet.hoursLogHours) > 0;
  const hasPayoutHours =
    timesheet.payoutHours !== null &&
    timesheet.payoutHours !== undefined &&
    Number(timesheet.payoutHours) !== 0;
  const hasManagerDelta = hasWorkerHours && hasManagerHours;
  const hasPayoutDelta = hasWorkerHours && hasPayoutHours;
  const hasCompleteHoursLogs = timesheet.workerShiftsHaveHoursLogs;
  const isPrePayout = ["pending", "approving"].includes(timesheet.status);
  const isNoHours = isNoHoursPayoutDashboardRow(timesheet, quickBooksExported);
  const showHoursLogWarning = hasManagerHours && !hasCompleteHoursLogs;
  const showHoursLogComplete = hasManagerHours && hasCompleteHoursLogs;
  const showDeltaHoursLogWarning = isPrePayout && showHoursLogWarning;
  const showDeltaHoursLogComplete = isPrePayout && showHoursLogComplete;
  const managerDelta =
    Number(timesheet.hoursLogHours || 0) - Number(timesheet.workerHours || 0);
  const payoutDelta =
    Number(timesheet.payoutHours || 0) - Number(timesheet.workerHours || 0);
  const managerDeltaTitle = showDeltaHoursLogWarning
    ? PAYOUT_TIMESHEET_TOOLTIPS.hoursLogWarning
    : showDeltaHoursLogComplete
      ? PAYOUT_TIMESHEET_TOOLTIPS.hoursLogComplete
      : hasManagerHours
        ? PAYOUT_TIMESHEET_TOOLTIPS.managerDeltaHeader
        : PAYOUT_TIMESHEET_TOOLTIPS.managerHoursMissing;
  const payoutDeltaTitle = hasPayoutHours
    ? PAYOUT_TIMESHEET_TOOLTIPS.payoutDeltaHeader
    : PAYOUT_TIMESHEET_TOOLTIPS.payoutHoursMissing;
  const hoursLogValueClass = showHoursLogWarning
    ? PAYOUT_TIMESHEET_TABLE.hoursValueClassName.warning
    : showHoursLogComplete
      ? PAYOUT_TIMESHEET_TABLE.hoursValueClassName.complete
      : "";
  const deltaValueClass = showDeltaHoursLogWarning
    ? PAYOUT_TIMESHEET_TABLE.hoursValueClassName.warning
    : showDeltaHoursLogComplete
      ? PAYOUT_TIMESHEET_TABLE.hoursValueClassName.complete
      : "";
  const status = getPayoutTimesheetStatusDisplay(timesheet.status, isNoHours);
  const statusTitle = isNoHours
    ? PAYOUT_TIMESHEET_TOOLTIPS.noHoursStatus
    : undefined;

  return {
    hasManagerHours,
    hasManagerDelta,
    hasPayoutDelta,
    showHoursLogWarning,
    showHoursLogComplete,
    showDeltaHoursLogWarning,
    managerDelta,
    payoutDelta,
    managerDeltaTitle,
    payoutDeltaTitle,
    hoursLogValueClass,
    deltaValueClass,
    status,
    statusTitle,
  };
}

/**
 * Resolves column-specific value/sort/render handlers.
 *
 * @param {string} key - Column key from config.
 * @param {boolean} quickBooksExported - Week QuickBooks export flag.
 * @returns {Object} SimpleTable value handlers for the column.
 */
function getColumnHandlers(key, quickBooksExported) {
  switch (key) {
    case "workerName":
      return {
        getValue: (row) => row.workerName,
        getSortValue: (row) => row.workerName || "",
        title: (row) => row.workerName,
      };
    case "shiftCount":
      return {
        getValue: (row) => formatDashboardCount(row.shiftCount),
        getSortValue: (row) => Number(row.shiftCount) || 0,
        render: (row) => formatDashboardCount(row.shiftCount),
      };
    case "primaryProject":
      return {
        getValue: (row) => row.primaryProject?.number || "",
        getSortValue: (row) => row.primaryProject?.number || "",
        // Custom chip owns its TooltipArrow hover popup.
        render: (row) => (
          <PrimaryJobChip
            primaryProject={row.primaryProject}
            projects={row.projects}
          />
        ),
      };
    case "workerHours":
      return {
        getValue: (row) => formatDashboardHours(row.workerHours),
        getSortValue: (row) =>
          row.workerHours === null || row.workerHours === undefined
            ? null
            : Number(row.workerHours),
        render: (row) => formatDashboardHours(row.workerHours),
      };
    case "hoursLogHours":
      return {
        getValue: (row) => {
          const state = getRowDisplayState(row, quickBooksExported);
          return state.hasManagerHours
            ? formatDashboardHours(row.hoursLogHours)
            : "-";
        },
        getSortValue: (row) => Number(row.hoursLogHours) || 0,
        // Preserve clock icon + warning/complete coloring via custom render.
        render: (row) => {
          const state = getRowDisplayState(row, quickBooksExported);
          return (
            <span
              className={`inline-flex items-center justify-end gap-1 ${state.hoursLogValueClass}`}
              title={
                state.showHoursLogWarning
                  ? PAYOUT_TIMESHEET_TOOLTIPS.hoursLogWarning
                  : state.showHoursLogComplete
                    ? PAYOUT_TIMESHEET_TOOLTIPS.hoursLogComplete
                    : PAYOUT_TIMESHEET_TOOLTIPS.managerHoursMissing
              }
            >
              {state.showHoursLogWarning ? (
                <Clock className="h-3 w-3 flex-shrink-0" />
              ) : null}
              {state.hasManagerHours
                ? formatDashboardHours(row.hoursLogHours)
                : "-"}
            </span>
          );
        },
      };
    case "payoutHours":
      return {
        getValue: (row) => formatDashboardHours(row.payoutHours),
        getSortValue: (row) =>
          row.payoutHours === null || row.payoutHours === undefined
            ? null
            : Number(row.payoutHours),
        render: (row) => formatDashboardHours(row.payoutHours),
      };
    case "managerDelta":
      return {
        getValue: (row) => {
          const state = getRowDisplayState(row, quickBooksExported);
          return state.hasManagerDelta
            ? formatDeltaDisplay(state.managerDelta)
            : "-";
        },
        getSortValue: (row) => {
          const state = getRowDisplayState(row, quickBooksExported);
          return state.hasManagerDelta ? state.managerDelta : null;
        },
        render: (row) => {
          const state = getRowDisplayState(row, quickBooksExported);
          return (
            <span
              className={`inline-flex items-center justify-end gap-1 ${state.deltaValueClass}`}
              title={state.managerDeltaTitle}
            >
              {state.showDeltaHoursLogWarning ? (
                <Clock className="h-3 w-3 flex-shrink-0" />
              ) : null}
              {state.hasManagerDelta
                ? formatDeltaDisplay(state.managerDelta)
                : "-"}
            </span>
          );
        },
      };
    case "payoutDelta":
      return {
        getValue: (row) => {
          const state = getRowDisplayState(row, quickBooksExported);
          return state.hasPayoutDelta
            ? formatDeltaDisplay(state.payoutDelta)
            : "-";
        },
        getSortValue: (row) => {
          const state = getRowDisplayState(row, quickBooksExported);
          return state.hasPayoutDelta ? state.payoutDelta : null;
        },
        title: (row) =>
          getRowDisplayState(row, quickBooksExported).payoutDeltaTitle,
        render: (row) => {
          const state = getRowDisplayState(row, quickBooksExported);
          return state.hasPayoutDelta
            ? formatDeltaDisplay(state.payoutDelta)
            : "-";
        },
      };
    case "status":
      return {
        getValue: (row) =>
          getRowDisplayState(row, quickBooksExported).status.label,
        getSortValue: (row) =>
          getRowDisplayState(row, quickBooksExported).status.label,
        render: (row) => {
          const state = getRowDisplayState(row, quickBooksExported);
          return (
            <span
              className={`${PAYOUT_TIMESHEET_TABLE.statusPillClassName} ${state.status.className}`}
              title={state.statusTitle}
            >
              {state.status.label}
            </span>
          );
        },
      };
    default:
      return {};
  }
}

/**
 * Builds SimpleTable column configs from payoutTimesheetTableConfig.
 *
 * @param {Object} options - Column builder options.
 * @param {boolean} [options.quickBooksExported=false] - Week QuickBooks export flag.
 * @returns {Array<Object>} SimpleTable column definitions.
 */
export function getPayoutTimesheetColumns({ quickBooksExported = false } = {}) {
  return PAYOUT_TIMESHEET_COLUMNS.map((column) => ({
    ...column,
    ...getColumnHandlers(column.key, quickBooksExported),
  }));
}
